import isPropValid from '@emotion/is-prop-valid';
import { ShellThemeProvider } from '@sede-enterprise/shell-ds-react-framework';
import { getAuthStoreAccess } from 'common-react/auth/auth-store';
import { AuthActionHooks, AuthProvider } from 'common-react/auth/AuthProvider';
import { DYNATRACE_ACTIONS } from 'constants/dynatrace-actions';
import { AuthApolloProvider } from 'pluto-graphql/apollo/auth-apollo-provider';
import { useEffect } from 'react';
import { BrowserRouter as Router } from 'react-router';
import { useStore } from 'store';
import { StyleSheetManager } from 'styled-components';
import {
  dynatraceTrackEndAction,
  dynatraceTrackStartAction,
} from 'utils/dynatrace';

// This implements the default behavior from styled-components v5, as a workaround until SDS updates to use styled components V6
function shouldForwardProp(propName: any, target: any) {
  if (typeof target === 'string') {
    // For HTML elements, forward the prop if it is a valid HTML attribute
    return isPropValid(propName);
  }
  // For other elements, forward all props
  return true;
}

const authStoreAccess = getAuthStoreAccess('shell-pluto');

export const authActionHooks: AuthActionHooks = {
  onStartLogin: () => {
    dynatraceTrackStartAction(DYNATRACE_ACTIONS.LOGIN);
  },
  onEndLogin: () => {
    dynatraceTrackEndAction(DYNATRACE_ACTIONS.LOGIN);
  },
  onStartLogout: () => {
    dynatraceTrackStartAction(DYNATRACE_ACTIONS.SIGN_OUT);
  },
  onEndLogout: () => {
    dynatraceTrackEndAction(DYNATRACE_ACTIONS.SIGN_OUT);
  },
};

function AppProviders({ children }: any) {
  const [themeMode, appTheme, setAppTheme] = useStore((state) => [
    state.themeMode,
    state.appTheme,
    state.setAppTheme,
  ]);

  useEffect(() => {
    if (themeMode === 'system') {
      window.matchMedia('(prefers-color-scheme: dark)').matches
        ? setAppTheme('dark')
        : setAppTheme('light');
    } else {
      setAppTheme(themeMode);
    }
  }, [themeMode, setAppTheme]);

  // updating scrollbar native gutter to match theme
  useEffect(() => {
    document.documentElement.style.display = 'none';
    // change scheme
    document.documentElement.setAttribute('data-color-scheme', appTheme);

    document.documentElement.style.display = '';
  }, [appTheme]);

  return (
    <StyleSheetManager
      shouldForwardProp={shouldForwardProp}
      enableVendorPrefixes
    >
      <ShellThemeProvider theme={appTheme}>
        <AuthProvider
          authStoreAccess={authStoreAccess}
          actionHooks={authActionHooks}
        >
          <AuthApolloProvider>
            <Router>{children}</Router>
          </AuthApolloProvider>
        </AuthProvider>
      </ShellThemeProvider>
    </StyleSheetManager>
  );
}

export { AppProviders };
